import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../common/Button';
// MKS-361 — which projects this absence affects, and the soft overlap warning.
import CoverageWarningList from '../time-off/CoverageWarningList';
import { useCoverageOptions } from '../../hooks/useTimeOffCoverage';
import { getHolidays } from '../../services/api/timeEntry';
import type {
  TimeOffAllowance,
  TimeOffType,
  CreateTimeOffInput,
  VacationBalance,
} from '../../services/api/timeOff';
import { drawsVacationBalance } from '../../services/api/timeOff';
import { useFlag } from '../../services/flagsContext';
import { parseDecimalInput } from '../../utils/parseDecimalInput';
import {
  formatBalanceHours,
  formatDaysAndHours,
  hasVacationEntitlement,
  requestStoredHours,
  storedHoursToReal,
} from '../../utils/vacationBalance';
import { DateField } from '../ui/DateField';

/**
 * MKS-327 — a day count for a yearly allowance. Half and quarter days are real
 * (the same types usually allow partial days), so one decimal is kept and a
 * whole number prints without a trailing `.0`.
 */
function formatAllowanceDays(days: number): string {
  const rounded = Math.round(days * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${text} ${Math.abs(rounded) === 1 ? 'day' : 'days'}`;
}

interface TimeOffFormProps {
  types: TimeOffType[];
  initialValues?: Partial<{
    timeOffTypeId: number;
    dateFrom: string;
    dateTo: string;
    dateFromHours: number;
    dateToHours: number;
  }>;
  mode?: 'create' | 'edit';
  /**
   * MKS-361 — the request being edited, so the coverage block resolves its
   * projects from the stored row. Absent on a new request, where they come
   * from the dates alone.
   */
  timeOffId?: number;
  /**
   * MKS-326 — the requester's vacation balance, when they have one.
   * Omitted (or all-null, for a contractor) means: no hour
   * conversion, no remaining-balance line, no warning.
   */
  balance?: VacationBalance | null;
  /**
   * MKS-327 — the requester's remaining days on every type with its own yearly
   * cap. Empty (or omitted) means no type has one, so nothing is shown.
   */
  allowances?: TimeOffAllowance[];
  onSubmit: (input: CreateTimeOffInput) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export default function TimeOffForm({ types, initialValues, mode = 'create', timeOffId, balance, allowances, onSubmit, onCancel, isLoading }: TimeOffFormProps) {
  // MKS-361 — the coverage block. Flag off: nothing is fetched and nothing is
  // rendered.
  const substitutesOn = useFlag('mks_361_time_off_substitutes');

  const [form, setForm] = useState({
    timeOffTypeId: initialValues?.timeOffTypeId != null ? String(initialValues.timeOffTypeId) : '',
    dateFrom: initialValues?.dateFrom ?? '',
    dateTo: initialValues?.dateTo ?? '',
    dateFromHours: initialValues?.dateFromHours != null ? String(initialValues.dateFromHours) : '8',
    dateToHours: initialValues?.dateToHours != null ? String(initialValues.dateToHours) : '8',
  });
  const [error, setError] = useState<string | null>(null);

  // Determine which years the date range spans for holiday fetching
  const years = useMemo(() => {
    if (!form.dateFrom || !form.dateTo) return [];
    const startYear = new Date(form.dateFrom).getFullYear();
    const endYear = new Date(form.dateTo).getFullYear();
    const result: number[] = [];
    for (let y = startYear; y <= endYear; y++) result.push(y);
    return result;
  }, [form.dateFrom, form.dateTo]);

  // Fetch Czech holidays for the relevant years
  const holidayQueries = useQuery({
    queryKey: ['holidays', ...years],
    queryFn: async () => {
      const sets = await Promise.all(years.map((y) => getHolidays(y)));
      const dates = new Set<string>();
      for (const res of sets) {
        for (const h of res.data) dates.add(h.date);
      }
      return dates;
    },
    enabled: years.length > 0,
    staleTime: 24 * 60 * 60 * 1000,
    structuralSharing: false,
  });

  // Calculate working days preview (excluding weekends and Czech holidays)
  const workingDays = useMemo(() => {
    if (!form.dateFrom || !form.dateTo) return null;
    const start = new Date(form.dateFrom);
    const end = new Date(form.dateTo);
    if (start > end) return null;
    const holidayDates = holidayQueries.data instanceof Set ? holidayQueries.data : new Set<string>();
    let count = 0;
    const current = new Date(start);
    while (current <= end) {
      const day = current.getDay();
      const dateStr = current.toISOString().slice(0, 10);
      if (day !== 0 && day !== 6 && !holidayDates.has(dateStr)) count++;
      current.setDate(current.getDate() + 1);
    }
    return count;
  }, [form.dateFrom, form.dateTo, holidayQueries.data]);

  /**
   * MKS-326 — what this request costs the vacation balance, and what is left
   * after it.
   *
   * The hour figures in the form are the stored fraction-of-standard-day
   * scale (8 = one whole working day). The balance is in the person's real
   * hours, so the request has to be converted through their day length before
   * the two can be compared — at 0.5 FTE a five-day request is 20 h of
   * entitlement, not the 40 h the raw figures suggest.
   *
   * Only the vacation type draws the balance; everything else (sick days,
   * unpaid leave) is unaffected and gets no conversion or warning.
   */
  /**
   * MKS-327 — the picked type, and therefore everything the form has to do
   * differently for it. A type is never matched by name or code here.
   */
  const selectedType = useMemo(
    () => types.find((t) => String(t.id) === form.timeOffTypeId),
    [types, form.timeOffTypeId]
  );

  /** Whole days only — the backend rejects a part day on such a type. */
  const partialDayAllowed = selectedType?.partialDayAllowed !== false;
  const isPresenceType = selectedType?.category === 'presence';

  /**
   * MKS-361 — the coverage block.
   *
   * Keyed on the dates and the type, so moving the range refetches the
   * projects rather than showing yesterday's. Skipped entirely for a
   * presence type: home office needs no stand-in, and the backend would
   * return both lists empty anyway — not asking is one fewer round trip and
   * one fewer flash of an empty box.
   *
   * Petra, 2026-10-19: the requester does NOT pick their substitute, so what
   * this feeds is the informational "Affects:" line and the soft overlap
   * warning. The pick itself is the PM's, on the approval queue.
   */
  const coverageQuery = useCoverageOptions(
    {
      dateFrom: form.dateFrom || undefined,
      dateTo: form.dateTo || undefined,
      timeOffTypeId: form.timeOffTypeId ? parseInt(form.timeOffTypeId, 10) : undefined,
      ...(timeOffId !== undefined ? { timeOffId } : {}),
    },
    substitutesOn && !isPresenceType,
  );
  const coverageProjects = useMemo(
    () => coverageQuery.data?.projects ?? [],
    [coverageQuery.data],
  );
  const coverageWarnings = coverageQuery.data?.warnings ?? [];

  /** `Acme – web (Developer), Acme – mobile (Developer)` — one plain line. */
  const affectedProjects = useMemo(
    () =>
      coverageProjects
        .map((project) =>
          project.roleName ? `${project.projectName} (${project.roleName})` : project.projectName,
        )
        .join(', '),
    [coverageProjects],
  );

  const vacationCheck = useMemo(() => {
    if (!hasVacationEntitlement(balance) || workingDays === null || workingDays <= 0) return null;
    if (!drawsVacationBalance(selectedType)) return null;

    const dayHours = balance.dailyWorkingHours;
    const stored = requestStoredHours(
      workingDays,
      parseDecimalInput(form.dateFromHours) ?? 8,
      parseDecimalInput(form.dateToHours) ?? 8
    );
    const requestHours = storedHoursToReal(stored, dayHours);

    // Measured against what is left AFTER everything already submitted: two
    // requests that each fit on their own can still overdraw together, and the
    // person would otherwise only find out when the second one is approved.
    const available = balance.remainingHours - balance.pendingHours;

    return {
      dayHours,
      requestHours,
      available,
      remainingAfter: available - requestHours,
      exceeds: requestHours > available,
    };
  }, [balance, selectedType, workingDays, form.dateFromHours, form.dateToHours]);

  /**
   * MKS-327 — the same check for a type with its OWN yearly cap in days (5
   * sick days, 12 home-office days). Days, not hours, and not FTE-scaled: a
   * cap of five days means five days for everyone.
   *
   * Like the vacation one this is a WARNING, never a block — the request still
   * submits and a manager or HR decides.
   */
  const allowanceCheck = useMemo(() => {
    if (workingDays === null || workingDays <= 0) return null;
    if (selectedType?.allowanceKind !== 'own_yearly_days') return null;

    const allowance = allowances?.find((a) => a.timeOffTypeId === selectedType.id);
    if (!allowance) return null;

    const storedHours = requestStoredHours(
      workingDays,
      parseDecimalInput(form.dateFromHours) ?? 8,
      parseDecimalInput(form.dateToHours) ?? 8
    );
    // The stored hour scale is 8 = one whole day, which is exactly how
    // timeOffAllowanceService counts a used day.
    const requestDays = storedHours / 8;
    const available = allowance.remainingDays - allowance.pendingDays;

    return {
      allowance,
      requestDays,
      available,
      remainingAfter: available - requestDays,
      exceeds: requestDays > available,
    };
  }, [allowances, selectedType, workingDays, form.dateFromHours, form.dateToHours]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.timeOffTypeId) {
      setError('Please select a type.');
      return;
    }
    if (!form.dateFrom || !form.dateTo) {
      setError('Please select start and end dates.');
      return;
    }
    if (new Date(form.dateFrom) > new Date(form.dateTo)) {
      setError('End date must be on or after start date.');
      return;
    }

    try {
      await onSubmit({
        timeOffTypeId: parseInt(form.timeOffTypeId, 10),
        dateFrom: form.dateFrom,
        dateTo: form.dateTo,
        // MKS-327: a whole-days-only type sends whole days whatever is left in
        // the (hidden) selects from a previously picked type — the backend
        // rejects anything else, and the user cannot see the fields to fix it.
        dateFromHours: partialDayAllowed ? (parseDecimalInput(form.dateFromHours) ?? 8) : 8,
        dateToHours: partialDayAllowed ? (parseDecimalInput(form.dateToHours) ?? 8) : 8,
      });
    } catch (err) {
      setError((err as Error).message || 'Failed to create time off request.');
    }
  };

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
      <h3 className="text-lg font-medium text-gray-900">
        {mode === 'edit' ? 'Edit Time Off Request' : 'New Time Off Request'}
      </h3>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {/* Type */}
      <div>
        <label htmlFor="timeoff-type" className="block text-sm font-medium text-gray-700 mb-1">Type</label>
        <select
          id="timeoff-type"
          value={form.timeOffTypeId}
          onChange={(e) => updateField('timeOffTypeId', e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          required
        >
          <option value="">Select type...</option>
          {/* MKS-327: an archived type cannot be picked. The list the backend
              serves already excludes them; this filter also covers the edit
              form, which may be rendered from a cached list. */}
          {types
            .filter((t) => !t.archived)
            .map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
        </select>
        {/* MKS-327: the two things about the picked type that change what the
            person has to do next. */}
        {isPresenceType && (
          <p className="mt-1 text-sm text-gray-500">
            You are working on these days — log your hours as usual.
          </p>
        )}
        {selectedType?.needsApproval === false && (
          <p className="mt-1 text-sm text-gray-500">
            No approval needed — this is confirmed as soon as you submit it, and your manager is
            notified.
          </p>
        )}
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="timeoff-date-from" className="block text-sm font-medium text-gray-700 mb-1">From</label>
          <DateField
            id="timeoff-date-from"
            value={form.dateFrom}
            onChange={(nextDate) => updateField('dateFrom', nextDate)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>
        <div>
          <label htmlFor="timeoff-date-to" className="block text-sm font-medium text-gray-700 mb-1">To</label>
          <DateField
            id="timeoff-date-to"
            value={form.dateTo}
            onChange={(nextDate) => updateField('dateTo', nextDate)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>
      </div>

      {/* MKS-327: a whole-days-only type (parental leave, a business trip) has
          no part-day choice to make, so the selects are not rendered at all —
          `handleSubmit` sends whole days for it. */}
      {!partialDayAllowed && (
        <p className="text-sm text-gray-500">This type is requested in whole days.</p>
      )}

      {/* Partial day hours.
          The option VALUES stay 8/4/2 — that is what lands in
          TimeOffDay.hours and what capacity/costs read. The labels drop the
          hour counts on purpose: the vacation balance draws
          `hours x employee FTE`, so for someone at 0.5 FTE a "full day" costs
          4 h, not 8. Printing "(8h)" told them otherwise. */}
      <div className={`grid grid-cols-2 gap-4${partialDayAllowed ? '' : ' hidden'}`}>
        <div>
          <label htmlFor="timeoff-from-hours" className="block text-sm font-medium text-gray-700 mb-1">First day</label>
          <select
            id="timeoff-from-hours"
            value={form.dateFromHours}
            onChange={(e) => updateField('dateFromHours', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="8">Full day</option>
            <option value="4">Half day</option>
            <option value="2">Quarter day</option>
          </select>
        </div>
        <div>
          <label htmlFor="timeoff-to-hours" className="block text-sm font-medium text-gray-700 mb-1">Last day</label>
          <select
            id="timeoff-to-hours"
            value={form.dateToHours}
            onChange={(e) => updateField('dateToHours', e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="8">Full day</option>
            <option value="4">Half day</option>
            <option value="2">Quarter day</option>
          </select>
        </div>
      </div>

      {/* Working days preview */}
      {workingDays !== null && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <p className="text-sm text-blue-800">
            <span className="font-medium">{workingDays}</span> working {workingDays === 1 ? 'day' : 'days'}
            {/* MKS-326: spell the cost out in the person's own hours. The
                select above talks in whole/half/quarter days precisely
                because a "day" is 8 h for one person and 4 h for another. */}
            {vacationCheck && (
              <>
                {' '}
                = <span className="font-medium">{formatBalanceHours(vacationCheck.requestHours)} h</span>
                {' '}at {formatBalanceHours(vacationCheck.dayHours)} h a day
              </>
            )}
            {form.dateFrom === form.dateTo ? '' : ` (excluding weekends and holidays)`}
          </p>
          {vacationCheck && (
            <p className="text-sm text-blue-800 mt-1">
              Vacation left after this request:{' '}
              <span className="font-medium">
                {formatDaysAndHours(vacationCheck.remainingAfter, vacationCheck.dayHours)}
              </span>
            </p>
          )}
          {/* MKS-327 — the same line for a type with its own yearly cap. In
              whole days, because that is the unit the cap is set in. */}
          {allowanceCheck && (
            <p className="text-sm text-blue-800 mt-1">
              {allowanceCheck.allowance.typeName ?? 'Allowance'} left after this request:{' '}
              <span className="font-medium">
                {formatAllowanceDays(allowanceCheck.remainingAfter)} of{' '}
                {formatAllowanceDays(allowanceCheck.allowance.allowanceDays)}
              </span>
            </p>
          )}
        </div>
      )}

      {/* MKS-326: a WARNING, never a block — the request still submits and
          HR/an admin can still approve it. Making it a hard stop is a
          deliberate later decision, not this one. */}
      {vacationCheck?.exceeds && (
        <div
          role="status"
          className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-800"
        >
          This request is {formatDaysAndHours(vacationCheck.requestHours, vacationCheck.dayHours)}{' '}
          but only {formatDaysAndHours(vacationCheck.available, vacationCheck.dayHours)} of vacation
          is left. You can still submit it — your manager or HR decides.
        </div>
      )}

      {/* MKS-327 — over the type's own yearly cap. Same contract as the
          vacation warning above: a warning, never a block. */}
      {allowanceCheck?.exceeds && (
        <div
          role="status"
          className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-800"
        >
          This request is {formatAllowanceDays(allowanceCheck.requestDays)} but only{' '}
          {formatAllowanceDays(allowanceCheck.available)} of your{' '}
          {formatAllowanceDays(allowanceCheck.allowance.allowanceDays)} yearly{' '}
          {allowanceCheck.allowance.typeName ?? 'allowance'} is left. You can still submit it —
          your manager or HR decides.
        </div>
      )}

      {/* MKS-361 — which projects this absence affects and in what role, plus
          the soft warning when a role on one of them would be left with
          nobody. Informational only: the substitute is picked by the project
          manager when they approve. Both only appear once there are dates to
          resolve projects from, and never for a presence type. */}
      {substitutesOn && !isPresenceType && (
        <>
          {coverageProjects.length > 0 && (
            <div className="pt-2 border-t border-gray-100 space-y-1">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Affects:</span> {affectedProjects}
              </p>
              <p className="text-sm text-gray-500">
                Your project manager picks who covers you when they approve this.
              </p>
            </div>
          )}
          <CoverageWarningList warnings={coverageWarnings} audience="requester" />
        </>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
        <Button variant="secondary" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {mode === 'edit' ? 'Save Changes' : 'Submit Request'}
        </Button>
      </div>
    </form>
  );
}
